import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  LightModeSemanticColors,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { Badge } from '~/components/common/Badge'
import {
  POSITION_TOOLTIP_BG_COLOR,
  POSITION_TOOLTIP_BORDER_COLOR,
  POSITION_TOOLTIP_POSITIVE_COLOR,
  POSITION_TOOLTIP_WARNING_COLOR,
  POSITION_UNKNOWN_ISSUER_COLOR,
} from '~/constants/chartPalette'
import { iconSize } from '~/constants/iconSize'
import { formatMw } from '~/models/mwDisplay'

import { type CoverageState, resolveCoverageState } from './coverageColors'
import type { ChartDatum, PositionChartColors } from './types'

// tooltip 用 token
const TIP_BG = POSITION_TOOLTIP_BG_COLOR
const TIP_BORDER = POSITION_TOOLTIP_BORDER_COLOR
const TIP_MUTED = LightModeSemanticColors.Text.midEmphasis.value // --text-mid-emphasis
const TIP_TEXT = LightModeSemanticColors.Text.highEmphasis.value // --text-high-emphasis
const TIP_POS = POSITION_TOOLTIP_POSITIVE_COLOR
const TIP_WARN = POSITION_TOOLTIP_WARNING_COLOR
// 旧 TIP_NEG (--text-negative-mid) は 段階色 (80/50) の廃止で参照元が無くなったため削除した
// (design `js/coverage-state.js` の 5 状態は base / positive / warning のみを使う)。

/**
 * tooltip の推定高さ (px)。 縦位置クランプ (= 後述 `resolveTooltipTranslateY`) の判定にだけ使う
 * 概算値で、 実測はしない (= 実測 → state 更新は hover ごとの再レンダリング/ちらつきを招く)。
 * 内訳: padding 16 + ヘッダ 26 + カバー率 46 + 数量 5 行 (調達必要量・入札済み・全札と、
 * それぞれの直下の行のうち時間前市場の 2 行ぶん / スポットの約定量 1 行ぶんを含めた概算) 120 +
 * 区切り 9 (+ クリックヒント 32)。
 * 過大評価側に倒しても「枠から少し内側に出る」だけで、 見切れる方向には効かない。
 */
const TOOLTIP_HEIGHT_BASE = 217
const TOOLTIP_HEIGHT_WITH_HINT = TOOLTIP_HEIGHT_BASE + 32
/**
 * スポットの通常/ブロック内訳行 (「うち 通常」「うち ブロック」、 AC-BSP-016) ぶんの
 * 追加高さ。 design `spot.html` の超過量行と同じ 1 行 22px の概算を 2 行ぶん足す
 * (design コメント「全行 (約定後カバー量 + うち通常/うちブロック 含む) の実測 ~224」
 * 参照)。 時間前市場 (内訳なし) では加算しない。
 */
const TOOLTIP_HEIGHT_EXECUTED_BREAKDOWN = 44
const TOOLTIP_HEIGHT_ROW = 22

/** カーソルと tooltip の間隔 (px) / 可視領域の端に残す余白 (px)。 */
const TOOLTIP_CURSOR_GAP = 12
const TOOLTIP_EDGE_MARGIN = 4

/**
 * recharts が tooltip wrapper に与える基準オフセット (px) = `<Tooltip offset={...}>` と同値。
 * wrapper 自体は「カーソル + offset」 に置かれ、 content 側の transform はそこからの相対になるため、
 * カーソル基準で位置を決める本 component 側で打ち消す必要がある。
 */
const TOOLTIP_RECHARTS_OFFSET = 16

/**
 * el を実際に縦方向でクリップする最も近い祖先の viewport 座標レンジを返す。
 *
 * ポジション chart は accordion 本文 → ページのスクロール領域 (overflow-y:auto) の中にあり、
 * tooltip は chart (h=160px) より高いので、 素の smart-flip だけだとスクロール枠の上端/下端で
 * 見切れる。 描画のたびにこの実レンジへクランプすることで「常に全体が見える」 を担保する。
 */
function resolveClipRange(el: HTMLElement | null): {
  top: number
  bottom: number
} {
  let top = 0
  let bottom = typeof window === 'undefined' ? 0 : window.innerHeight
  for (
    let node = el?.parentElement ?? null;
    node !== null;
    node = node.parentElement
  ) {
    const overflowY = window.getComputedStyle(node).overflowY
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'hidden'
    ) {
      const rect = node.getBoundingClientRect()
      top = Math.max(top, rect.top)
      bottom = Math.min(bottom, rect.bottom)
    }
  }
  return { top, bottom }
}

/**
 * tooltip content に与える縦 transform (= recharts wrapper 位置からの px) を決める。
 *
 * 既定は design `spotChartTooltip` の smart-flip (= カーソルが chart の下半分なら上向き) だが、
 * 出した結果が可視領域 ({@link resolveClipRange}) からはみ出す場合は上下にクランプして全体を見せる。
 * 可視領域自体が tooltip より低い場合のみ上端優先で残りが切れる (= これ以上は縮められない)。
 */
export function resolveTooltipTranslateY(params: {
  /** カーソルの viewport y 座標 (px)。 */
  cursorViewportY: number
  /** chart 内でのカーソルの縦相対位置 (0=上端 / 1=下端)。 */
  relY: number
  /** tooltip の推定高さ (px)。 */
  tooltipHeight: number
  clip: { top: number; bottom: number }
}): number {
  const { cursorViewportY, relY, tooltipHeight, clip } = params
  const preferUp = relY >= 0.5
  const desiredTop =
    cursorViewportY +
    (preferUp ? -(tooltipHeight + TOOLTIP_CURSOR_GAP) : TOOLTIP_CURSOR_GAP)
  const maxTop = clip.bottom - tooltipHeight - TOOLTIP_EDGE_MARGIN
  const minTop = clip.top + TOOLTIP_EDGE_MARGIN
  // 可視領域が tooltip より低いときは minTop > maxTop になるので上端側を優先する。
  const clampedTop = Math.max(minTop, Math.min(desiredTop, maxTop))
  // wrapper は「カーソル + TOOLTIP_RECHARTS_OFFSET」 に置かれているのでその分を差し引く。
  return clampedTop - (cursorViewportY + TOOLTIP_RECHARTS_OFFSET)
}

/**
 * 充足率の状態 key (= {@link resolveCoverageState} の判定結果)。 判定の SoT は
 * `~/components/bidding/common/coverageColors` 側 (tooltip とバッジで写経しない)。
 *
 * **語そのものは返さない。** 表示側が `page:biddingCommon.coverageState.<key>` を
 * 引く (以前は日本語を直値で返しており、 EN ロケールでも日本語が出ていた)。
 */
export function resolveCoverageStateKey(
  pct: number | undefined,
  needMw: number,
): CoverageState['key'] {
  return resolveCoverageState(pct, needMw).key
}

/** 充足率テキスト色 ({@link resolveCoverageState} の tone → tooltip の色)。 */
function resolveCoverageColor(pct: number | undefined, needMw: number): string {
  switch (resolveCoverageState(pct, needMw).tone) {
    case 'warning':
      return TIP_WARN
    case 'positive':
      return TIP_POS
    case 'neutral':
      return TIP_MUTED
    default:
      return TIP_TEXT
  }
}

// ----- tooltip 本体 (= design `spotChartTooltip` / `intradayChartTooltip` 準拠) -----
// 構成: ヘッダ (コマ + 時間レンジ) / カバー率プログレスバー / 数量 3 行 / クリックヒント。

type TooltipRenderProps = {
  active?: boolean
  label?: string | number
  payload?: ReadonlyArray<{ payload?: ChartDatum }>
  // recharts が content 要素へ注入する cursor 座標 / plot 領域 (= smart-flip 計算用)。
  coordinate?: { x?: number; y?: number }
  viewBox?: { x?: number; y?: number; width?: number; height?: number }
}

/* eslint-disable ebalance/no-adhoc-typography -- chart tooltip の typography は
   design/bidding/spot.html の spotChartTooltip 関数 (3568-3697) が SoT で raw px
   直書き体系 (11px/18px の design スケール外値を含む)。ページの役割 textStyle
   体系の対象外 */

/**
 * 数量 1 行 (label 左 muted、 value 右 系列色)。 value が null なら未入札として「—」。
 *
 * **[design 差異]** 表示は小数 1 桁 (`formatMw`)。 design (`spot.html` の
 * `spotChartTooltip` / `intraday.html` の `intradayChartTooltip`) は `Math.round` で
 * 整数丸めのままだが、 数量の表示精度は spec AC (AC-BSP-053 / AC-BIN-043 = 小数 1 桁)
 * が SoT なので そちらに合わせる (issue #6492。 design 側の追従は Des レーン)。
 */
const QuantityRow = ({
  label,
  value,
  color,
  emphasis = false,
  badge,
  indent = false,
}: {
  label: string
  value: number | null
  color: string
  emphasis?: boolean
  /** ラベルの横に出すバッジの文字 (入札時カバー率 `N%`)。 value が null の行では出さない。 */
  badge?: string
  /** 直上の行の内訳 (「うち 超過量」「うち 入札後残量」) として字下げする。 */
  indent?: boolean
}): React.ReactElement => {
  const isNum = value !== null
  return (
    <Flex
      alignItems="baseline"
      justifyContent="space-between"
      gap="12px"
      padding="2px 0"
      fontSize="sm"
    >
      <Flex
        alignItems="baseline"
        gap="6px"
        minWidth={0}
        paddingLeft={indent ? '1em' : undefined}
      >
        <Text as="span" color={TIP_MUTED}>
          {label}
        </Text>
        {isNum && badge !== undefined && (
          <Badge
            flexShrink={0}
            alignSelf="center"
            height="18px"
            fontSize="xs"
            paddingX="6px"
            status="neutral"
          >
            {badge}
          </Badge>
        )}
      </Flex>
      <Text
        as="span"
        color={isNum ? color : TIP_MUTED}
        fontSize={emphasis ? 'lg' : 'md'}
        fontWeight={emphasis ? 'bold' : 'semibold'}
        css={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {isNum ? formatMw(value) : '—'}
        {isNum && (
          <Text
            as="span"
            marginLeft="3px"
            color={TIP_MUTED}
            fontSize="xs"
            fontWeight="normal"
          >
            {' '}
            MW
          </Text>
        )}
      </Text>
    </Flex>
  )
}

/**
 * カバー率メーター (プログレスバー or —)。 「全札（下書き含む）」基準の 1 本だけを出し
 * (design `spot.html` / `intraday.html` の rateNode)、 「入札済み」のカバー率は数量行の
 * バッジで示す (AC-BIN-105 / AC-BSP-108)。
 * 0〜100 のメーターでは 208% も −80% も表現できない (design `bidding/spot.html` tooltip
 * と同じ扱い):
 *   100% 超 … バーは振り切らせ、 「100% だった位置」 にノッチを立てて超過の度合いを見せる
 *   マイナス … メーターは向きを表現できないので描かず、 実値 % +「逆方向」 の語に委ねる
 */
const CoverageRateRow = ({
  label,
  pct,
  color,
  stateLabel,
  isPlaceholder,
}: {
  label: string
  pct: number | undefined
  color: string
  stateLabel: string
  /** このコマに札が無い (0件) または pct===undefined (need=0) のとき true (= 「—」表示)。 */
  isPlaceholder: boolean
}): React.ReactElement => {
  if (isPlaceholder || pct === undefined) {
    return (
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        marginBottom="6px"
      >
        <Text as="span" color={TIP_MUTED} fontSize="sm">
          {label}
        </Text>
        <Text as="span" color={TIP_MUTED} fontSize="md" fontWeight="semibold">
          —
        </Text>
      </Flex>
    )
  }
  return (
    <Box marginBottom="6px">
      <Flex
        alignItems="baseline"
        justifyContent="space-between"
        marginBottom="4px"
      >
        <Text as="span" color={TIP_MUTED} fontSize="sm">
          {label}
        </Text>
        <Text as="span">
          <Text
            as="span"
            color={color}
            fontSize="lg"
            fontWeight="bold"
            css={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {pct}%
          </Text>
          <Text
            as="span"
            marginLeft="6px"
            color={color}
            fontSize="xs"
            fontWeight="semibold"
          >
            {stateLabel}
          </Text>
        </Text>
      </Flex>
      {pct >= 0 && (
        <Box
          position="relative"
          overflow="hidden"
          height="6px"
          background="rgba(0, 0, 0, 0.08)"
          borderRadius="full"
        >
          <Box
            width={`${Math.min(100, pct)}%`}
            height="100%"
            background={color}
            borderRadius="full"
          />
          {pct > 100 && (
            <Box
              position="absolute"
              top="0"
              bottom="0"
              left={`${(100 / pct) * 100}%`}
              width="2px"
              background={TIP_BG}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

/**
 * カバー量の行の直下に出す内訳 1 行。 過剰入札のコマは超過量、 そうでなければ入札後残量
 * (時間前市場のみ。 スポットは超過量だけ)。 超過量と入札後残量は符号が逆の同じ量なので
 * 行数を増やさずどちらか一方だけ出す (design `intraday.html` の tooltip)。
 * need 取得不可 (`remainMw` が undefined) の時の入札後残量は「—」。
 */
const CoverSurplusRow = ({
  isIntraday,
  overMw,
  remainMw,
}: {
  isIntraday: boolean
  overMw: number | undefined
  remainMw: number | undefined
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (overMw !== undefined) {
    return (
      <QuantityRow
        color={TIP_WARN}
        indent
        label={t('page:biddingCommon.chart.overLabel')}
        value={overMw}
      />
    )
  }
  if (!isIntraday) return null
  return (
    <QuantityRow
      color={TIP_TEXT}
      indent
      label={t('page:biddingCommon.chart.remainBreakdownLabel')}
      value={remainMw ?? null}
    />
  )
}

export const PositionTooltip = ({
  active,
  label,
  payload,
  coordinate,
  viewBox,
  isEmpty,
  showHint,
  gcBoundarySlotIndex,
  colors,
  chartAreaRef,
}: TooltipRenderProps & {
  /**
   * 入札 0 件 (= 全コマで買・売とも 0)。 スポットの約定量の行を「—」 にする判定。 入札時カバー率・
   * 数量の「—」 判定は 2 本の線それぞれ、 ホバーしたコマの `ChartDatum.isEmptyAll` /
   * `isEmptySubmitted` で行う (AC-BIN-105 / AC-BSP-108)。
   */
  isEmpty: boolean
  /** クリックヒント (= クリックして入札追加) を表示するか (= onSlotClick あり)。 */
  showHint: boolean
  /** 入札締切の境界 slot index。 これ未満のコマは入札締切後 (= クリックしても作成不可) のヒント表示にする。 */
  gcBoundarySlotIndex?: number
  /** 数量 3 行の系列色 (= chart 本体と同一 palette)。 */
  colors: PositionChartColors
  /** chart 描画領域の DOM 参照 (= 縦位置クランプで viewport 座標を得るために使う)。 */
  chartAreaRef: React.RefObject<HTMLDivElement | null>
}): React.ReactElement | null => {
  const { t } = useTranslation()
  if (active !== true || payload === undefined || payload.length === 0) {
    return null
  }
  const d = payload[0]?.payload
  if (d === undefined) return null
  const emptyAll = d.isEmptyAll
  const emptySubmitted = d.isEmptySubmitted

  // ヘッダ: "コマ N" + "HH:MM〜HH:MM"
  const m = String(label ?? '').match(/^(\d{1,2}):(\d{2})$/)
  let komaLabel = ''
  let rangeLabel = ''
  let koma: number | undefined
  if (m !== null) {
    const h = Number(m[1])
    const mn = Number(m[2])
    koma = h * 2 + (mn >= 30 ? 2 : 1)
    let nxMn = mn + 30
    let nxH = h
    if (nxMn >= 60) {
      nxMn -= 60
      nxH += 1
    }
    const pad = (n: number): string => String(n).padStart(2, '0')
    // 「コマ {{index}}」 は既存 biddingCommon.ribbon.slotPrefix と同一文言のため再利用する。
    komaLabel = t('page:biddingCommon.ribbon.slotPrefix', { index: koma })
    rangeLabel = `${String(label)}〜${pad(nxH)}:${pad(nxMn)}`
  }

  // 入札時カバー率 (= 入札時カバー量 / 調達必要量、 クランプなし)。 0件 / need=0 は「—」。
  // メーターは「全札」基準の 1 本、 「入札済み」は数量行のバッジ (AC-BIN-105 / AC-BSP-108)。
  const pct = d.coveragePct
  const rateColor = resolveCoverageColor(pct, d.need)
  const rateLabel = t(
    `page:biddingCommon.coverageState.${resolveCoverageStateKey(pct, d.need)}`,
  )
  const pctBadge = (
    value: number | undefined,
    empty: boolean,
  ): string | undefined =>
    !empty && value !== undefined ? `${value}%` : undefined
  const isIntraday = colors.bar === 'remain'

  // 入札締切後 (= 締切境界コマ未満) はクリックしても作成不可なので、 ヒントを「締切後」 に切替える。
  const isClosed =
    gcBoundarySlotIndex !== undefined &&
    koma !== undefined &&
    koma < gcBoundarySlotIndex

  // クリックヒント: 不足→買 / 余剰→売 / 差分 MW。 discriminant を先に決め、
  // 訳文 (「買」「売」 は既存 common:tradeSide.* と同一文言) は clickHintWithDiff の
  // dir 補間直前だけで解決する (文字列比較で分岐を判定しない)。
  const dirKind: 'buy' | 'sell' | null = d.needAbsent
    ? null
    : d.need > d.cover
      ? 'buy'
      : d.cover > d.need
        ? 'sell'
        : null
  const diff = Math.abs(d.need - d.cover)
  const hintText = isClosed
    ? t('page:biddingCommon.chart.closedHint')
    : dirKind !== null && diff > 0
      ? t('page:biddingCommon.chart.clickHintWithDiff', {
          dir: t(
            dirKind === 'buy'
              ? 'common:tradeSide.buy'
              : 'common:tradeSide.sell',
          ),
          diff: diff.toFixed(1),
        })
      : t('page:biddingCommon.chart.clickHint')

  // smart-flip (= design `spotChartTooltip` の transform)。
  // chart が低い (= 160px) ため、 カーソルが下半分なら tooltip を上向きに出す。
  // 右半分なら左寄せにして右端の見切れも防ぐ。
  const chartWidth = typeof viewBox?.width === 'number' ? viewBox.width : 0
  const chartLeft = typeof viewBox?.x === 'number' ? viewBox.x : 0
  const chartHeight = typeof viewBox?.height === 'number' ? viewBox.height : 0
  const chartTop = typeof viewBox?.y === 'number' ? viewBox.y : 0
  const cursorX = typeof coordinate?.x === 'number' ? coordinate.x : 0
  const cursorY = typeof coordinate?.y === 'number' ? coordinate.y : 0
  const relX = chartWidth > 0 ? (cursorX - chartLeft) / chartWidth : 0
  const relY = chartHeight > 0 ? (cursorY - chartTop) / chartHeight : 0
  const translateX = relX > 0.55 ? '-100%' : '0'
  // スポットの通常/ブロック内訳行 (AC-BSP-016) を出すかどうか。 combineSpotPositionSlots
  // が埋める 2 フィールドが両方揃っているときだけ (= 時間前市場は undefined のまま)。
  const hasExecutedBreakdown =
    d.executedNormal !== undefined && d.executedBlock !== undefined
  const showsUnknownIssuer =
    d.unknownIssuer !== undefined && d.unknownIssuer !== 0

  // 縦は smart-flip の向きを起点に、 実際の可視領域へクランプして全体が見えるようにする
  // (= chart より高い tooltip がスクロール枠の上下端で切れるのを防ぐ)。
  const chartAreaEl = chartAreaRef.current
  const chartAreaTop =
    chartAreaEl !== null ? chartAreaEl.getBoundingClientRect().top : 0
  const translateY = `${resolveTooltipTranslateY({
    cursorViewportY: chartAreaTop + cursorY,
    relY,
    tooltipHeight:
      (showHint ? TOOLTIP_HEIGHT_WITH_HINT : TOOLTIP_HEIGHT_BASE) +
      (hasExecutedBreakdown ? TOOLTIP_HEIGHT_EXECUTED_BREAKDOWN : 0) +
      (showsUnknownIssuer ? TOOLTIP_HEIGHT_ROW : 0),
    clip: resolveClipRange(chartAreaEl),
  })}px`

  return (
    <Box
      minWidth="180px"
      maxWidth="240px"
      padding="8px 10px"
      color={TIP_TEXT}
      lineHeight={1.4}
      background={TIP_BG}
      border={`1px solid ${TIP_BORDER}`}
      borderRadius="lg"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.18)"
      transform={`translate(${translateX}, ${translateY})`}
      pointerEvents="none"
    >
      {m !== null && (
        <Flex
          alignItems="baseline"
          justifyContent="space-between"
          gap="12px"
          marginBottom="6px"
          paddingBottom="6px"
          borderBottom={`1px solid ${TIP_BORDER}`}
        >
          <Text as="span" fontSize="md" fontWeight="bold">
            {komaLabel}
          </Text>
          <Text as="span" color={TIP_MUTED} fontSize="sm">
            {rangeLabel}
          </Text>
        </Flex>
      )}

      {/* カバー率メーター (プログレスバー or —)。 「全札（下書き含む）」基準の 1 本 */}
      <Box marginBottom="2px">
        <CoverageRateRow
          color={rateColor}
          isPlaceholder={emptyAll || pct === undefined}
          label={t('page:biddingCommon.chart.coverageRateLabel')}
          pct={pct}
          stateLabel={rateLabel}
        />
      </Box>

      {/* 数量 (need の行は常に出し、 need が無いコマ (AC-BSP-102) は「—」。 入札時カバー量は「入札済み」「全札（下書き含む）」の 2 本で、
          それぞれのカバー率をバッジで添え、 0件のコマは「—」。 各行の直下に、 過剰なら超過量・
          そうでなければ (時間前市場のみ) 入札後残量を出す。 AC-BIN-105 / AC-BSP-108) */}
      <QuantityRow
        color={colors.need}
        label={t('page:biddingCommon.chart.needLabel')}
        value={d.needAbsent ? null : d.need}
      />
      <QuantityRow
        color={colors.coverSubmitted}
        badge={pctBadge(d.coverageSubmittedPct, emptySubmitted)}
        label={t('page:biddingCommon.chart.coverSubmittedLabel')}
        value={emptySubmitted ? null : d.coverSubmitted}
      />
      <CoverSurplusRow
        isIntraday={isIntraday}
        overMw={emptySubmitted ? undefined : d.overSubmittedMw}
        remainMw={d.remainSubmitted}
      />
      <QuantityRow
        color={colors.cover}
        badge={pctBadge(pct, emptyAll)}
        label={t('page:biddingCommon.chart.coverAllLabel')}
        value={emptyAll ? null : d.cover}
      />
      <CoverSurplusRow
        isIntraday={isIntraday}
        overMw={emptyAll ? undefined : d.overMw}
        remainMw={d.remain}
      />
      {showsUnknownIssuer && (
        <QuantityRow
          color={POSITION_UNKNOWN_ISSUER_COLOR}
          label={t('page:biddingCommon.chart.unknownIssuerLabel')}
          value={d.unknownIssuer ?? 0}
        />
      )}
      {/* 約定量はスポットだけ (AC-BIN-012a: 時間前市場は「約定後カバー量 (executed) は
          指標として設けない」。 入札後残量は上の各線の直下に出す)。 */}
      {colors.bar === 'executed' && (
        <Box
          marginTop="4px"
          paddingTop="4px"
          borderTop={`1px dashed ${TIP_BORDER}`}
        >
          {/* AC-BSP-016b:「約定後カバー量」は指標として設けない（2026-09-02 用語統一。 決定
              経緯は spec Discussion 表「『約定後カバー量（executed）』指標の要否」参照）。
              スポット (= 通常/ブロック分割描画。 `hasExecutedBreakdown`) はこの合算行を
              design 正本 (`spot.html:4075-4077`) と同じ「約定量」に改称する。 */}
          <QuantityRow
            color={colors.barColor}
            emphasis
            label={
              hasExecutedBreakdown
                ? t('page:biddingCommon.chart.executedTotalLabel')
                : t('page:biddingCommon.chart.executedLabel')
            }
            value={isEmpty ? null : d.executed}
          />
          {/* 通常/ブロックの内訳 (= 積み上げ棒の色と対応、 AC-BSP-016)。 スポットのみ表示
              (`hasExecutedBreakdown` = combineSpotPositionSlots が埋める 2 フィールドの
              有無で判定)。 */}
          {hasExecutedBreakdown && (
            <>
              <QuantityRow
                color={colors.barColor}
                label={t(
                  'page:biddingCommon.chart.executedNormalBreakdownLabel',
                )}
                value={isEmpty ? null : (d.executedNormal ?? 0)}
              />
              <QuantityRow
                color={colors.executedBlock ?? colors.barColor}
                label={t(
                  'page:biddingCommon.chart.executedBlockBreakdownLabel',
                )}
                value={isEmpty ? null : (d.executedBlock ?? 0)}
              />
            </>
          )}
        </Box>
      )}
      {/* クリック動線ヒント (= chart コマ click → 札作成 modal) */}
      {showHint && (
        <Flex
          alignItems="center"
          justifyContent="center"
          gap="4px"
          width="100%"
          marginTop="6px"
          padding="3px 8px"
          color={TIP_MUTED}
          fontSize="sm"
          fontWeight="semibold"
          border={`1px solid ${TIP_BORDER}`}
          borderRadius="md"
          pointerEvents="none"
        >
          {!isClosed && (
            <FaPlusRegular boxSize={iconSize.gridInline} aria-hidden="true" />
          )}
          {hintText}
        </Flex>
      )}
    </Box>
  )
}

/* eslint-enable ebalance/no-adhoc-typography */
